/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { UnifiedCollectionScreenType } from 'client-code/actions';
import { EpisodeInfo } from 'client-code/episode-guide';
import PropTypes from 'prop-types';

import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { UIOrchestrator, UIOrchestratorState } from '../../../core/uiorchestrator';
import { ListTypes } from '../../../core/utils';
import { CollectionFilters } from '../../../store/collectionscreen/CollectionFilters';
import { setCollectionPageLoading } from '../../../store/collectionscreen/collectionscreen.actions';
import {
    getCollectionScreenInfo,
    getFiltersInfo,
    getPickerStripFromSeasons,
    getSeasonsDataForSelectedFilter,
    logError,
} from '../../../store/collectionscreen/collectionscreen.reducers';
import { PickerStripData } from '../../../store/collectionscreen/PickerStripData';
import { PickerStripType } from '../../../store/collectionscreen/PickerStripType';
import { List, ListCollectionItem, Mousetrap } from '../../common';
import { CollectionItemsProps } from './CollectionScreenItemsProps';

const CollectionScreenItems: FunctionComponent<CollectionItemsProps> = ({
    selectedFilter,
}): JSX.Element => {
    const TAG = 'CollectionScreenItems';
    const FIRST_PAGE_MAX_INDEX = 9;
    const PAGE_MAX_INDEX = 10;
    const dispatch = useTivoDispatch();
    const unifiedCollectionId = useRef<string>('');
    const pickerStripData = useRef<string[]>([]);
    const pickerStripMap = useRef<Map<number, PickerStripData>>(new Map());
    const itemPositionRef = useRef<number | undefined>(undefined);

    const [tempDeletedItem, setTempDeletedItem] = useState<number | undefined>(undefined);
    const { filters, isLoadingFilterItems, isLoadingPage, isLastPage } = useTivoSelector(
        (state: RootState) => state.collectionScreen,
    );
    const {
        episodes,
        isDeleteButtonEnable,
        isGoToEpisodeButtonEnable,
        offset,
        sortOrder,
        isExactPageScrolled,
    } = filters[selectedFilter];
    const itemslistEntry = useTivoSelector(
        (state: RootState) => state.list[ListTypes.COLLECTION_ITEMS],
    );
    const itemPosition = itemslistEntry?.position;
    itemPositionRef.current = itemPosition;

    const fetchSeasonData = async () => {
        try {
            if (
                selectedFilter === CollectionFilters.NONE ||
                selectedFilter === CollectionFilters.GET_THIS_SHOW_DETAILS ||
                selectedFilter === CollectionFilters.ONEPASS_OPTIONS_DETAIL ||
                selectedFilter === CollectionFilters.UPCOMING_DETAIL
            ) {
                dispatch(setCollectionPageLoading(false));
                return;
            }
            const seasonInfo = await getSeasonsDataForSelectedFilter(selectedFilter, sortOrder);
            pickerStripMap.current = getPickerStripFromSeasons(seasonInfo);
            dispatch(setCollectionPageLoading(false));
        } catch (error: any) {
            dispatch(setCollectionPageLoading(false));
            Log.e(
                TAG,
                `CollectionScreenItems: Error fetching season data for filter ${selectedFilter}`,
            );
            logError(TAG, error);
        }
    };

    const getUnifiedCollectionId = () => {
        unifiedCollectionId.current = getCollectionScreenInfo()?.unifiedCollectionId || '';
    };

    const handleCollectionScreenItemUIStateChange = (state: UIOrchestratorState) => {
        if (
            state === UIOrchestratorState.BOOKMARK_TEMP_DELETED ||
            state === UIOrchestratorState.RECORDING_TEMP_DELETED
        ) {
            // itemPositionRef was introduced to prevent stale closure here.
            setTempDeletedItem(itemPositionRef.current);
        } else if (
            state === UIOrchestratorState.BOOKMARK_DELETED ||
            state === UIOrchestratorState.RECORDING_DELETED ||
            state === UIOrchestratorState.BOOKMARK_DELETION_FAILED ||
            state === UIOrchestratorState.RECORDING_DELETION_FAILED
        ) {
            setTempDeletedItem(undefined);
        }
    };

    useEffect(() => {
        getUnifiedCollectionId();
        fetchSeasonData();

        UIOrchestrator.stateChangeListener.subscribe(handleCollectionScreenItemUIStateChange);

        return () => {
            UIOrchestrator.stateChangeListener.cancel(handleCollectionScreenItemUIStateChange);
        };
    }, []);

    useEffect(() => {
        fetchSeasonData();
    }, [selectedFilter]);

    const renderSeasonPickerItem = (
        item: any,
        itemClass: string,
        index: number,
        horizontalIndex: number,
        isCursorInteractable: boolean,
    ) => {
        const pickerStyle =
            !episodes[itemPosition]?.contentId && horizontalIndex === itemPosition ? itemClass : '';
        const RenderingTag = isCursorInteractable ? Mousetrap : 'div';
        return (
            <>
                <div key={`cs-picker-item-${index}`} className="cs-picker-item-layout">
                    <RenderingTag
                        key={`pickeritem-${index}`}
                        value={`${index}_${ListTypes.COLLECTION_SEASON_PICKER}_${horizontalIndex}`}
                        className={`cs-picker-item ${pickerStyle}`}
                    >
                        {item}
                    </RenderingTag>
                </div>
            </>
        );
    };
    const renderSeasonPicker = (
        pickerItem: EpisodeInfo,
        horizontalIndex: number,
        isCursorInteractable: boolean,
    ) => {
        const pickerStrip = pickerStripMap.current.get(pickerItem?.seasonOrYear || 0);
        pickerStripData.current = pickerStrip?.stripData || [];
        const isFirstStrip =
            pickerStripMap.current.keys().next().value === pickerItem?.seasonOrYear || false;
        if (isLoadingPage) {
            return <div className="cs-picker-layout" />;
        }
        return (
            <>
                {!isFirstStrip && (
                    <div
                        key={`isFirstStrip-${horizontalIndex}`}
                        className="cs-season-seprator-line"
                    />
                )}
                <div key={`cs-picker-layout-${horizontalIndex}`} className="cs-picker-layout">
                    <div className="cs-season-text-width">{pickerStrip?.title || ''}</div>
                    {pickerStrip?.type === PickerStripType.PICKER_STRIP && (
                        <>
                            <div className="cs-jump-to-text-width">
                                {pickerStrip?.subTitle || ''}
                            </div>

                            <List
                                type={ListTypes.COLLECTION_SEASON_PICKER}
                                itemList={pickerStripData.current}
                                isHorizontalList={true}
                                generateItemView={(item, itemClass, index) =>
                                    renderSeasonPickerItem(
                                        item,
                                        itemClass,
                                        index || 0,
                                        horizontalIndex,
                                        isCursorInteractable,
                                    )
                                }
                            />
                        </>
                    )}
                </div>
            </>
        );
    };

    const getEmptyListMessage = (): string => {
        const filtersInfo = getFiltersInfo();
        switch (selectedFilter) {
            case CollectionFilters.ALL_EPISODE_DETAIL:
            case CollectionFilters.RECORDINGS_DETAILS:
            case CollectionFilters.WATCH_LIST_DETAILS:
            case CollectionFilters.ON_DEMAND_EPISODES_UNIFIED_COLLECTION_DETAIL:
                return Locale.getString(LocaleKeys.EMPTY_LIST_TEXT);
            case CollectionFilters.UPCOMING_DETAIL:
                return filtersInfo?.unifiedCollectionScreenType ===
                    UnifiedCollectionScreenType.TeamScreenDetails ||
                    filtersInfo?.unifiedCollectionScreenType ===
                        UnifiedCollectionScreenType.LeagueScreenDetails
                    ? Locale.getString(LocaleKeys.COLLECTION_FILTER_UPCOMING_LEAGUE_TEAM_EMPTY)
                    : Locale.getString(LocaleKeys.COLLECTION_FILTER_UPCOMING_EMPTY);
            default:
                return '';
        }
    };

    const renderListItem = (item: any, itemClass: string, index: number) => {
        const buttonStyle = itemPosition === index ? 'selected' : 'hidden';
        const buttonStyleFocus = 'selected focused';
        // Make the last element non selectable
        const RenderingTag =
            (offset === 0 && index === FIRST_PAGE_MAX_INDEX && !isExactPageScrolled) ||
            (offset > 0 && index === PAGE_MAX_INDEX)
                ? 'div'
                : Mousetrap;
        if (selectedFilter !== CollectionFilters.UPCOMING_DETAIL && !item?.contentId) {
            return renderSeasonPicker(item, index, RenderingTag !== 'div');
        }
        return (
            <div key={`container-${index}`} className="cs-item-layout">
                <RenderingTag
                    key={`item-${index}`}
                    value={`${index}_${ListTypes.COLLECTION_ITEMS}`}
                    className={`item ${(isGoToEpisodeButtonEnable || isDeleteButtonEnable) && itemPosition === index ? 'selected' : itemClass}`}
                >
                    <ListCollectionItem
                        collectionItemData={item || {}}
                        isSelected={itemPosition === index}
                        isFocused={
                            !isGoToEpisodeButtonEnable &&
                            !isDeleteButtonEnable &&
                            !!itemslistEntry?.isFocused
                        }
                        isDeleted={tempDeletedItem === index}
                        collectionFilterType={selectedFilter}
                        sortOrderType={sortOrder}
                    />
                </RenderingTag>
                {(itemslistEntry?.isFocused || itemPosition === index) &&
                    selectedFilter !== CollectionFilters.UPCOMING_DETAIL && (
                        <RenderingTag
                            key={`cs-icon-go-to-episode-${index}`}
                            value={`${index}_${ListTypes.COLLECTION_ITEMS}_goto`}
                            className={`cs-icon-container ${isGoToEpisodeButtonEnable && itemPosition === index ? buttonStyleFocus : buttonStyle}`}
                        >
                            <div className="cs-icon-container">
                                <span className="cs-icon-go-to-episode" />
                            </div>
                        </RenderingTag>
                    )}
                {(itemslistEntry?.isFocused || itemPosition === index) &&
                    selectedFilter !== CollectionFilters.UPCOMING_DETAIL &&
                    selectedFilter !== CollectionFilters.ALL_EPISODE_DETAIL &&
                    selectedFilter !==
                        CollectionFilters.ON_DEMAND_EPISODES_UNIFIED_COLLECTION_DETAIL && (
                        <RenderingTag
                            key={`cs-delete-icon-${index}`}
                            value={`${index}_${ListTypes.COLLECTION_ITEMS}_delete`}
                            className={`cs-icon-container ${isDeleteButtonEnable && itemPosition === index ? buttonStyleFocus : buttonStyle}`}
                        >
                            <div className="cs-icon-container">
                                <span className="cs-delete-icon" />
                            </div>
                        </RenderingTag>
                    )}
            </div>
        );
    };

    return (
        <div
            className={
                offset === 0 && !isLastPage && !isExactPageScrolled
                    ? 'cs-items-layout'
                    : 'cs-items-layout-scroll-view'
            }
        >
            <List
                type={ListTypes.COLLECTION_ITEMS}
                itemList={episodes}
                generateItemView={(item, itemClass, index) =>
                    renderListItem(item, itemClass, index || 0)
                }
            />
            {!isLoadingFilterItems && episodes.length === 0 && (
                <div className="cs-empty-list">{getEmptyListMessage()}</div>
            )}
        </div>
    );
};

CollectionScreenItems.propTypes = {
    selectedFilter: PropTypes.string.isRequired,
};

export default CollectionScreenItems;
